import { useEffect, useState } from 'react';
import { ApiRequest, ShowLoader } from 'GlobalFunctions';
import * as _ from 'lodash';
import { enqueueSnackbar } from "notistack";
import AutocompleteCities from '../../utils/AutocompleteCities';
import { createElement } from 'react';
import { getShippingMethods } from './Shipping';
import Coupons from './Coupons';

// Define type for custom inputs
type CustomInput = {
    name: string;
    value: string | boolean;
};

const defaultSubscriptionValues = {
    // Client information is now grouped in a client object
    client: {
        name: "",
        lastname: "",
        country: "",
        state: "",
        city: "",
        city_id: "",
        postal: "",
        email: "",
        phone: "",
        custom_inputs: [] as CustomInput[]
    }
    // Removed validation states from default values
};

// Create an in-memory validation state object that is not stored in localStorage
const validationState = {
    isValidCardName: false,
    isValidCardNumber: false,
    isValidExpiration: false,
    isValidCVV: false,
    isValidEmail: false,
    isValidPhone: false
};

export const validateEmail = (email: string): boolean => {
    const re = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/;
    return re.test(email);
};

export const validatePhone = (phone: string): boolean => {
    const re = /^\+?[1-9]\d{1,14}$/;
    return re.test(phone);
};

export const formatCardNumber = (value: string): string => {
    return value
        .replace(/\D/g, '') // Remove non-digits
        .replace(/(.{4})/g, '$1 ') // Add space after every 4 digits
        .trim() // Remove trailing spaces
        .substring(0, 19); // Limit to 16 digits + 3 spaces
};

export const formatExpirationDate = (value: string): string => {
    let cleanedValue = value.replace(/\D/g, '');
    if (cleanedValue.length > 2) {
        cleanedValue = cleanedValue.substring(0, 2) + '/' + cleanedValue.substring(2);
    }
    return cleanedValue.substring(0, 5);
};

export const validateExpirationDate = (value: string): boolean => {
    const parts = value.split('/');
    if (parts.length !== 2) return false;
    
    const month = parseInt(parts[0], 10);
    const year = parseInt(parts[1], 10);
    
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear() % 100;
    const currentMonth = currentDate.getMonth() + 1;
    
    if (month < 1 || month > 12) return false;
    if (year < currentYear) return false;
    if (year === currentYear && month < currentMonth) return false;
    
    return true;
};

export const validateCardNumber = (cardNumber: string): boolean => {
    // Check if the card number has the correct length (16 digits + 3 spaces = 19 characters)
    // This matches the validation in SubscriptionForm.tsx
    return cardNumber.length >= 19;
};

export const initializeSubscription = (): void => {
    // Set style="position:relative" on the subscription popup inner container BEFORE any other logic
    const popupInner = document.querySelector('[pc="subscription-wrapper"]') as HTMLElement;
    if (popupInner) {
        popupInner.style.position = 'relative';
    }

    // Get subscription data from localStorage or use defaults
    const storedData = localStorage.getItem('subscription');
    let subscriptionData: any = defaultSubscriptionValues;
    
    try {
        if (storedData) {
            const parsedData = JSON.parse(storedData);
            
            // Handle upgrading from old format to new format with client object
            if (!parsedData.client) {
                // Migrate existing data to new structure
                parsedData.client = {
                    name: parsedData.client_name || "",
                    lastname: parsedData.client_lastname || "",
                    country: parsedData.country || "",
                    state: parsedData.state || "",
                    city: parsedData.city || "",
                    city_id: parsedData.city_id || "",
                    postal: parsedData.postal || "",
                    email: parsedData.email || "",
                    phone: parsedData.phone || "",
                    custom_inputs: parsedData.custom_inputs || []
                };
                
                // Remove old properties
                delete parsedData.client_name;
                delete parsedData.client_lastname;
                delete parsedData.country;
                delete parsedData.state;
                delete parsedData.city;
                delete parsedData.city_id;
                delete parsedData.postal;
                delete parsedData.email;
                delete parsedData.phone;
                delete parsedData.custom_inputs;
            }
            
            // Clean up data before merging with defaults
            const cleanData = cleanupDataForStorage(parsedData);
            subscriptionData = { ...defaultSubscriptionValues, ...cleanData };
            
            // Calculate total_price
            if (subscriptionData.products && Array.isArray(subscriptionData.products)) {
                const sum = subscriptionData.products.reduce((acc: number, product: any) => {
                    const price = parseFloat(product.product_price || "0");
                    return acc + (isNaN(price) ? 0 : price);
                }, 0);
                subscriptionData.subtotal = sum.toFixed(2);
            } else if (!subscriptionData.subtotal) {
                subscriptionData.subtotal = "0.00";
            }
        }
    } catch (error) {
        console.error('Error parsing subscription data from localStorage:', error);
    }
    
    // Find the subscription form to collect all custom inputs
    const subscriptionForm = document.querySelector('[pc="subscription-form"]');
    if (subscriptionForm) {
        // Find all form elements without pc attribute (custom inputs)
        const customInputElements = Array.from(subscriptionForm.querySelectorAll('input, select, textarea'))
            .filter(element => !element.hasAttribute('pc') && element.hasAttribute('name'));
        
        // Capture elements with pc-custom attribute
        const pcCustomElements = Array.from(document.querySelectorAll('[pc-custom]'));
        
        // Initialize client.custom_inputs array if it doesn't exist
        if (!subscriptionData.client.custom_inputs) {
            subscriptionData.client.custom_inputs = [];
        }
        
        // Initialize all custom input elements with empty values if they don't exist
        customInputElements.forEach(element => {
            const elementName = (element as HTMLInputElement).name;
            if (!elementName) return;
            
            // Check if this custom input already exists in the array
            const existingIndex = subscriptionData.client.custom_inputs.findIndex(
                (item: CustomInput) => item.name === elementName
            );
            
            // If it doesn't exist, add it with an empty value
            if (existingIndex === -1) {
                let initialValue: string | boolean = '';
                if (element instanceof HTMLInputElement && element.type === 'checkbox') {
                    initialValue = false;
                }
                
                subscriptionData.client.custom_inputs.push({
                    name: elementName,
                    value: initialValue
                });
            }
        });
        
        // Initialize all pc-custom elements with empty values if they don't exist
        pcCustomElements.forEach(element => {
            const elementName = element.getAttribute('pc-custom');
            if (!elementName) return;
            
            // Check if this custom input already exists in the array
            const existingIndex = subscriptionData.client.custom_inputs.findIndex(
                (item: CustomInput) => item.name === elementName
            );
            
            // If it doesn't exist, add it with an empty value
            if (existingIndex === -1) {
                subscriptionData.client.custom_inputs.push({
                    name: elementName,
                    value: ''
                });
            }
        });
        
        // Save the updated data with initialized custom inputs to localStorage
        // Apply cleanup before saving
        localStorage.setItem('subscription', JSON.stringify(cleanupDataForStorage(subscriptionData)));
    }
    
    // Initialize form fields with values from localStorage
    // Handle client fields with the new structure
    const clientFields = {
        'name': 'fname',
        'lastname': 'lname',
        'country': 'country',
        'state': 'state',
        'city': 'city',
        'postal': 'postal',
        'email': 'email',
        'phone': 'phone'
    };
    
    // Set client fields
    Object.entries(clientFields).forEach(([key, formField]) => {
        const element = document.querySelector(`[pc="${formField}"]`) as HTMLInputElement;
        if (element && subscriptionData.client[key]) {
            element.value = subscriptionData.client[key].toString();
        }
    });

    // Recalcular impuestos si los datos de dirección están completos al abrir el popup
    if (
        subscriptionData.client &&
        subscriptionData.client.country &&
        subscriptionData.client.state &&
        subscriptionData.client.city &&
        subscriptionData.client.postal
    ) {
        checkTaxesAndShipping();
    }

    // We no longer initialize payment fields from localStorage
    // Payment fields will only be validated at form submission
    
    // Initialize custom input fields with values from localStorage
    if (subscriptionData.client.custom_inputs && Array.isArray(subscriptionData.client.custom_inputs)) {
        const subscriptionForm = document.querySelector('[pc="subscription-form"]');
        
        subscriptionData.client.custom_inputs.forEach((item: any) => {
            const { name, value } = item;
            if (!name) return;
            
            // Find elements by name attribute
            const elements = subscriptionForm?.querySelectorAll(`[name="${name}"]`);
            elements?.forEach(element => {
                if (element instanceof HTMLInputElement) {
                    if (element.type === 'checkbox') {
                        element.checked = !!value;
                    } else if (element.type === 'radio') {
                        element.checked = element.value === value;
                    } else {
                        element.value = value.toString();
                    }
                } else if (element instanceof HTMLSelectElement || element instanceof HTMLTextAreaElement) {
                    element.value = value.toString();
                }
            });
        });
    }
    
    initializeSubscriptionLocationFields();
    mountSubscriptionCityAutocomplete(subscriptionData.client, (newValues: any) => {
        // Update the subscription data with new values
        if (typeof newValues.city !== 'undefined') {
            subscriptionData.client.city = newValues.city;
        }
        if (typeof newValues.city_id !== 'undefined') {
            subscriptionData.client.city_id = newValues.city_id;
        }
        localStorage.setItem('subscription', JSON.stringify(cleanupDataForStorage(subscriptionData)));
    }
    );

    //initialize coupons
    Coupons(getTaxes)

    // Attach event listeners to all subscription form inputs
    attachSubscriptionEventListeners();
    
};

export const attachSubscriptionEventListeners = (): void => {
    // Find all elements with pc attribute
    const subscriptionElements = document.querySelectorAll('[pc]');
    
    subscriptionElements.forEach(element => {
        const pcSubValue = element.getAttribute('pc');
        
        // Skip elements that don't have values we want to track
        if (!pcSubValue || 
            pcSubValue === 'subscription-popup' || 
            pcSubValue === 'payment-form' || 
            pcSubValue === 'subscription-form' ||
            pcSubValue === 'summary-price' ||
            pcSubValue === 'recurrent-amount' ||
            pcSubValue === 'cancel-subscription') {
            return;
        }
        
        // For input elements, attach input event listener
        if (element instanceof HTMLInputElement || 
            element instanceof HTMLTextAreaElement || 
            element instanceof HTMLSelectElement) {
            
            // Remove any existing listeners to avoid duplicates
            element.removeEventListener('input', handleSubscriptionInputChange as EventListener);
            element.removeEventListener('change', handleSubscriptionInputChange as EventListener);
            
            // Add new listeners
            if (element instanceof HTMLSelectElement) {
                element.addEventListener('change', handleSubscriptionInputChange as EventListener);
            } else {
                element.addEventListener('input', handleSubscriptionInputChange as EventListener);
            }
            
            // Add special handling for card fields
            if (pcSubValue === 'card-number') {
                element.addEventListener('input', formatCardNumberOnInput as EventListener);
            } else if (pcSubValue === 'card-expiration') {
                element.addEventListener('input', formatExpirationDateOnInput as EventListener);
            }
        }
    });
    
    // Find and attach listeners to custom inputs in the subscription form
    const subscriptionForm = document.querySelector('[pc="subscription-form"]');
    if (subscriptionForm) {
        // Remove submit listener for subscription form
        // subscriptionForm.removeEventListener('submit', handleSubscriptionSubmit as EventListener);
        // subscriptionForm.addEventListener('submit', handleSubscriptionSubmit as EventListener);
        
        // Find the button inside subscription form
        const addButton = subscriptionForm.querySelector('[pc="add-btn"]');
        if (addButton) {
            addButton.removeEventListener('click', handleSubscriptionSubmit as EventListener);
            addButton.addEventListener('click', handleSubscriptionSubmit as EventListener);
        }
        
        // Find all form elements without pc attribute
        const customInputs = Array.from(subscriptionForm.querySelectorAll('input, select, textarea'))
            .filter(element => !element.hasAttribute('pc'));
            
        // Attach event listeners to these custom inputs
        customInputs.forEach(element => {
            if (element instanceof HTMLInputElement || 
                element instanceof HTMLTextAreaElement || 
                element instanceof HTMLSelectElement) {
                
                // Remove existing listeners to avoid duplicates
                element.removeEventListener('input', handleCustomInputChange as EventListener);
                element.removeEventListener('change', handleCustomInputChange as EventListener);
                
                // Add appropriate listeners based on element type
                if (element instanceof HTMLSelectElement || 
                    (element instanceof HTMLInputElement && 
                     (element.type === 'checkbox' || element.type === 'radio'))) {
                    element.addEventListener('change', handleCustomInputChange as EventListener);
                } else {
                    element.addEventListener('input', handleCustomInputChange as EventListener);
                }
            }
        });
    }
    
    // Attach click event to cancel button
    const cancelButton = document.querySelector('[pc="cancel-subscription"]');
    if (cancelButton) {
        cancelButton.removeEventListener('click', handleCancelSubscription as EventListener);
        cancelButton.addEventListener('click', handleCancelSubscription as EventListener);
    }
    
    const subscriptionElements2 = document.querySelectorAll('[pc-sub-element]');
    subscriptionElements2.forEach(element => {
        if (element instanceof HTMLSelectElement) {
            element.removeEventListener('change', handleSubscriptionInputChange as EventListener);
            element.addEventListener('change', handleSubscriptionInputChange as EventListener);
        }
    });
};

const handleSubscriptionInputChange = (event: Event): void => {
    const input = event.target as HTMLInputElement | HTMLSelectElement;
    
    // Check if the input is within the subscription wrapper
    const subscriptionWrapper = input.closest('[pc="subscription-wrapper"]');
    if (!subscriptionWrapper) return;
    
    const pcSubValue = input.getAttribute('pc');
    
    if (!pcSubValue) return;
    
    // Get current subscription data
    const storedData = localStorage.getItem('subscription');
    let subscriptionData: any = defaultSubscriptionValues;
    
    try {
        if (storedData) {
            subscriptionData = JSON.parse(storedData);
        }
    } catch (error) {
        console.error('Error parsing subscription data:', error);
    }
    
    
    type ClientKeys = keyof typeof defaultSubscriptionValues.client;
    const clientFields: Record<string, ClientKeys> = {
        'fname': 'name',
        'lname': 'lastname',
        'country': 'country',
        'state': 'state',
        'city': 'city',
        'postal': 'postal',
        'email': 'email',
        'phone': 'phone'
    };
    
    const value = input.value;
    
    // Handle payment fields separately - validate but don't store values or validation state
    if (pcSubValue === 'card-name') {
        validationState.isValidCardName = value.trim().length > 0;
    } 
    else if (pcSubValue === 'card-number') {
        validationState.isValidCardNumber = validateCardNumber(value);
    }
    else if (pcSubValue === 'card-expiration') {
        validationState.isValidExpiration = validateExpirationDate(value);
    }
    else if (pcSubValue === 'card-cvv') {
        validationState.isValidCVV = value.trim().length >= 3;
    }
    // Update client fields that can be stored
    else if (pcSubValue in clientFields) {
        const clientKey = clientFields[pcSubValue] as ClientKeys;
        subscriptionData.client[clientKey] = value;
        
        // Perform validation for email and phone but only store in memory
        if (clientKey === 'email') {
            validationState.isValidEmail = validateEmail(value);
        } else if (clientKey === 'phone') {
            validationState.isValidPhone = validatePhone(value);
        }
    }
    
    // Save ONLY client data to localStorage - no validation states
    localStorage.setItem('subscription', JSON.stringify(cleanupDataForStorage(subscriptionData)));
    
    // Check if the changed field is one of the address fields
    if (['country','state','city','postal'].includes(pcSubValue)) {
        // Call debouncedGetTaxes instead of getTaxes directly
        if (
            subscriptionData.client.country &&
            subscriptionData.client.state &&
            subscriptionData.client.city &&
            subscriptionData.client.postal
        ) {
            checkTaxesAndShipping(); 
        }
    }
    
};

const formatCardNumberOnInput = (event: Event): void => {
    const input = event.target as HTMLInputElement;
    const formattedValue = formatCardNumber(input.value);
    
    // Only update if the value actually changed to prevent cursor jumping
    if (formattedValue !== input.value) {
        const cursorPosition = input.selectionStart;
        const lengthDiff = formattedValue.length - input.value.length;
        
        input.value = formattedValue;
        
        // Update validation state in memory only
        validationState.isValidCardNumber = validateCardNumber(formattedValue);
        
        // Maintain cursor position
        if (cursorPosition !== null) {
            input.setSelectionRange(cursorPosition + lengthDiff, cursorPosition + lengthDiff);
        }
    }
};

const formatExpirationDateOnInput = (event: Event): void => {
    const input = event.target as HTMLInputElement;
    const formattedValue = formatExpirationDate(input.value);
    
    // Only update if the value actually changed
    if (formattedValue !== input.value) {
        const cursorPosition = input.selectionStart;
        const lengthDiff = formattedValue.length - input.value.length;
        
        input.value = formattedValue;
        
        // Update validation state in memory only
        validationState.isValidExpiration = validateExpirationDate(formattedValue);
        
        // Maintain cursor position
        if (cursorPosition !== null) {
            input.setSelectionRange(cursorPosition + lengthDiff, cursorPosition + lengthDiff);
        }
    }
};

// --- Country/State/City Initialization for Subscription Form (ClientForm style) ---
function getCountriesSubscription() {
    return new Promise<void>((resolve, reject) => {
        ApiRequest({
            url: '/countries/getCountries',
            method: 'GET',
            setResponse: (response: any) => {
                fillCountrySelectSubscription('country', response);
                resolve();
            }
        });
    });
}

function fillCountrySelectSubscription(pcAttribute: string, countries: any[]) {
    const selectElement = document.querySelector(`[pc="${pcAttribute}"]`) as HTMLSelectElement;
    if (selectElement) {
        countries.forEach((country: any) => {
            const option = document.createElement('option');
            option.value = country.country_id;
            option.innerHTML = country.country_name;
            selectElement.appendChild(option);
        });
    }
}

function clearStateSelectSubscription(selectElement: HTMLSelectElement) {
    const stateSelect = getStateSelectSubscription(selectElement);
    if (stateSelect) {
        stateSelect.innerHTML = '<option value="">Select State</option>';
    }
}

function getStateSelectSubscription(selectElement: HTMLSelectElement) {
    const pcValue = selectElement.getAttribute('pc');
    if (pcValue === 'country') {
        return document.querySelector('[pc="state"]') as HTMLSelectElement;
    }
    return null;
}

function getRegionsSubscription(country_id: any, selectElement: HTMLSelectElement, selectedRegionId?: any) {
    return new Promise<void>((resolve, reject) => {
        ApiRequest({
            url: '/countries/getRegions',
            method: 'GET',
            query: { country_id },
            setResponse: async (response: any) => {
                const stateSelect = getStateSelectSubscription(selectElement);
                if (stateSelect) {
                    stateSelect.innerHTML = '<option value="">Select State</option>';
                    (response.list || response).forEach((state: any) => {
                        const option = document.createElement('option');
                        option.value = state.region_id;
                        option.innerHTML = state.region_name;
                        stateSelect.appendChild(option);
                    });
                    if (selectedRegionId) {
                        stateSelect.value = selectedRegionId.toString();
                        const event = new Event('change', { bubbles: true });
                        stateSelect.dispatchEvent(event);
                    }
                }
                resolve();
            }
        });
    });
}

// --- Initialization function for subscription form ---
export function initializeSubscriptionLocationFields() {
    getCountriesSubscription().then(() => {
        const countrySelect = document.querySelector('[pc="country"]') as HTMLSelectElement;
        const stateSelect = document.querySelector('[pc="state"]') as HTMLSelectElement;
        if (countrySelect) {
            countrySelect.addEventListener('change', (event: Event) => {
                const target = event.target as HTMLSelectElement;
                clearStateSelectSubscription(countrySelect);
                getRegionsSubscription(target.value, countrySelect);
                const cityInput = document.querySelector('[pc="city"]') as HTMLInputElement;
                if (cityInput) cityInput.value = '';
            });
        }
        if (stateSelect) {
            stateSelect.addEventListener('change', () => {
                const cityInput = document.querySelector('[pc="city"]') as HTMLInputElement;
                if (cityInput) cityInput.value = '';
                // Recargar el componente AutocompleteCities con el nuevo estado
                // Obtener los valores actuales del formulario
                const storedData = localStorage.getItem('subscription');
                let subscriptionData: any = defaultSubscriptionValues;
                try {
                    if (storedData) {
                        subscriptionData = JSON.parse(storedData);
                    }
                } catch (error) {
                    // ignore
                }
                // Limpiar el valor de city en los datos
                if (subscriptionData.client) {
                    subscriptionData.client.city = '';
                    subscriptionData.client.city_id = '';
                    // Guardar el cambio en localStorage
                    localStorage.setItem('subscription', JSON.stringify(cleanupDataForStorage(subscriptionData)));
                }
                // Remover el contenedor anterior si existe
                const prevContainer = document.getElementById('subscription-city-autocomplete');
                if (prevContainer && prevContainer.parentNode) {
                    prevContainer.parentNode.removeChild(prevContainer);
                }
                // Volver a montar el componente AutocompleteCities
                mountSubscriptionCityAutocomplete(subscriptionData.client, (newValues: any) => {
                    subscriptionData.client.city = newValues.city;
                    subscriptionData.client.city_id = newValues.city_id;
                    localStorage.setItem('subscription', JSON.stringify(cleanupDataForStorage(subscriptionData)));
                });
            });
        }
    });
}



export function mountSubscriptionCityAutocomplete(formValues: any, setFormValues: any) {
    // Encuentra el input original
    const cityInput = document.querySelector('input[pc="city"]');
    if (!cityInput) return;

    // Crea un contenedor para el autocomplete
    const container = document.createElement('div');
    container.id = 'subscription-city-autocomplete';
    container.style.display = 'inline-block';

    // Inserta el contenedor justo DESPUÉS del input (para mantener el orden visual)
    if (cityInput.parentNode) {
        if (cityInput.nextSibling) {
            cityInput.parentNode.insertBefore(container, cityInput.nextSibling);
        } else {
            cityInput.parentNode.appendChild(container);
        }
    }

    // Monta el componente React en el contenedor
    import('react-dom/client').then(ReactDOMClient => {
        // Create root once and store it
        const root = ReactDOMClient.createRoot(container);
        
        // Use the root to render your component
        root.render(
            createElement(AutocompleteCities, {
                formValues,
                setFormValues: (newValues: any) => {
                    
                    // Get current subscription data
                    const storedData = localStorage.getItem('subscription');
                    let subscriptionData: any = defaultSubscriptionValues;
                    try {
                        if (storedData) {
                            subscriptionData = JSON.parse(storedData);
                        }
                    } catch (error) {
                        console.error('Error parsing subscription data:', error);
                    }
                    
                    // Handle the function-based update that AutocompleteCities returns
                    if (typeof newValues === 'function') {
                        // Create a dummy formValues object that matches what AutocompleteCities expects
                        const dummyFormValues = { city: subscriptionData.client.city, city_id: subscriptionData.client.city_id };
                        
                        // Execute the function to get the updates
                        const updatedValues = newValues(dummyFormValues);
                        
                        // Extract updates using the pattern from AutocompleteCities
                        if (updatedValues.city !== undefined) {
                            subscriptionData.client.city = updatedValues.city;
                        }
                        
                        if (updatedValues.city_id !== undefined) {
                            subscriptionData.client.city_id = updatedValues.city_id;
                        }

                        localStorage.setItem('subscription', JSON.stringify(cleanupDataForStorage(subscriptionData)));
                    }
                },
                pc_city: 'city',
                pc_state: 'state',
                attributeName: 'pc'
            })
        );
    });
}


// Add a function to collect custom inputs from the form
const getCustomInputs = (form: HTMLFormElement): { name: string, value: string | boolean }[] => {
    // Get current subscription data
    const storedData = localStorage.getItem('subscription');
    let subscriptionData: any = defaultSubscriptionValues;
    
    try {
        if (storedData) {
            subscriptionData = JSON.parse(storedData);
            
            // If we already have custom_inputs in localStorage, return it
            if (subscriptionData.client.custom_inputs && Array.isArray(subscriptionData.client.custom_inputs)) {
                return subscriptionData.client.custom_inputs;
            }
        }
    } catch (error) {
        console.error('Error parsing subscription data:', error);
    }
    
    // If no custom_inputs in localStorage, collect them from the form
    // Get inputs from form elements excluding those with pc attribute
    const customInputs: any = Array.from(form.elements)
        .filter((element: any) => !element.hasAttribute('pc'))
        .map((element: any) => {
            let value;
            if (element.type === 'checkbox') {
                if (!element.checked) return null;
                value = element.checked;
            } else if (element.type === 'radio') {
                if (element.checked) {
                    value = element.value;
                } else {
                    return null;
                }
            } else {
                value = element.value;
            }
            // Exclude inputs without a name attribute
            if (!element.name) return null;
            
            return {    
                name: element.name,
                value: value
            };
        })
        .filter(input => input !== null);

    // Capture elements with pc-custom attribute
    const pcCustomElements: any = Array.from(document.querySelectorAll('[pc-custom]'))
        .map((element) => {
            const htmlElement = element as HTMLElement;
            const name = htmlElement.getAttribute('pc-custom');
            const value = htmlElement.innerText.trim();
            
            if (!name) return null;
            
            return {
                name: name,
                value: value
            };
        })
        .filter(input => input !== null);

    // Combine all inputs
    return [...customInputs, ...pcCustomElements];
};


// Add the missing handleCancelSubscription function
const handleCancelSubscription = (event: Event): void => {
    event.preventDefault();
    
    // Hide the popup
    const popup = document.querySelector('[pc="subscription-popup"]') as HTMLElement;
    if (popup) {
        popup.style.display = 'none';
    }
    
    // Clear subscription data
    localStorage.removeItem('subscription');
};

// Add a new function to handle changes in custom inputs
const handleCustomInputChange = (event: Event): void => {
    const input = event.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
    
    // Skip elements without a name
    if (!input.name) return;
    
    // Get the name and value
    const name = input.name;
    let value;
    
    // Handle different input types
    if (input instanceof HTMLInputElement && input.type === 'checkbox') {
        value = input.checked;
    } else if (input instanceof HTMLInputElement && input.type === 'radio') {
        // Only update for checked radio buttons
        if (!input.checked) return;
        value = input.value;
    } else {
        value = input.value;
    }
    
    // Get current subscription data
    const storedData = localStorage.getItem('subscription');
    let subscriptionData: any = defaultSubscriptionValues;
    
    try {
        if (storedData) {
            subscriptionData = JSON.parse(storedData);
        }
    } catch (error) {
        console.error('Error parsing subscription data:', error);
    }
    
    // Initialize client.custom_inputs array if it doesn't exist
    if (!subscriptionData.client.custom_inputs) {
        subscriptionData.client.custom_inputs = [];
    }
    
    // Find if this input already exists in the custom_inputs array
    const existingIndex = subscriptionData.client.custom_inputs.findIndex(
        (item: any) => item.name === name
    );
    
    // Update or add the custom input
    if (existingIndex !== -1) {
        subscriptionData.client.custom_inputs[existingIndex].value = value;
    } else {
        subscriptionData.client.custom_inputs.push({ name, value });
    }
    
    // Save updated data to localStorage (with cleanup)
    localStorage.setItem('subscription', JSON.stringify(cleanupDataForStorage(subscriptionData)));
};



/*===============================================
SUBMIT
===============================================*/
const handleSubscriptionSubmit = (event: Event): void => {
    event.preventDefault();
    event.stopPropagation();
    // Get stored subscription data (without payment info or validation states)
    const subscriptionData = localStorage.getItem('subscription');
    if (!subscriptionData) {
        enqueueSnackbar('No subscription data found', { variant: 'error' });
        return;
    }
    
    const data = JSON.parse(subscriptionData);
    
    // Collect payment info directly from form fields at submission time
    // Keep existing payment_info properties if they exist
    const existingPaymentInfo = data.payment_info || {};
    const cardNameField = document.querySelector('[pc="card-name"]') as HTMLInputElement;
    const cardNumberField = document.querySelector('[pc="card-number"]') as HTMLInputElement;
    const cardExpirationField = document.querySelector('[pc="card-expiration"]') as HTMLInputElement;
    const cardCvvField = document.querySelector('[pc="card-cvv"]') as HTMLInputElement;
    
    const payment_info = {
        ...existingPaymentInfo, // Preserve any existing payment_info properties
        card_name: cardNameField?.value || '',
        card_number: cardNumberField?.value || '',
        card_expiration: cardExpirationField?.value || '',
        card_cvv: cardCvvField?.value || ''
    };
    
    // Validate all fields at submission time (don't rely on stored validation states)
    const isValidCardName = payment_info.card_name.trim().length > 0;
    const isValidCardNumber = validateCardNumber(payment_info.card_number);
    const isValidExpiration = validateExpirationDate(payment_info.card_expiration);
    const isValidCVV = payment_info.card_cvv.trim().length >= 3;
    const isValidEmail = validateEmail(data.client.email);
    const isValidPhone = validatePhone(data.client.phone);
    
    // Validate client fields
    const requiredClientFields = [
        { key: 'name', label: 'First Name' },
        { key: 'lastname', label: 'Last Name' },
        { key: 'country', label: 'Country' },
        { key: 'postal', label: 'Postal Code' },
        { key: 'email', label: 'Email' },
        { key: 'phone', label: 'Phone' }
    ];
    
    // Validate payment fields
    const requiredPaymentFields = [
        { key: 'card_name', label: 'Name on Card' },
        { key: 'card_number', label: 'Card Number' },
        { key: 'card_expiration', label: 'Card Expiration' },
        { key: 'card_cvv', label: 'CVV' }
    ];
    
    let validationErrors = [];
    
    // Collect missing client fields
    const missingClientFields = requiredClientFields.filter(field => !data.client[field.key]);
    if (missingClientFields.length > 0) {
        validationErrors.push(`Required fields are missing: ${missingClientFields.map(f => f.label).join(', ')}`);
    }
    
    // Collect missing payment fields
    const missingPaymentFields = requiredPaymentFields.filter(field => !payment_info[field.key]);
    if (missingPaymentFields.length > 0) {
        validationErrors.push(`Required fields are missing: ${missingPaymentFields.map(f => f.label).join(', ')}`);
    }
    
    // Validate email format
    if (data.client.email && !validateEmail(data.client.email)) {
        validationErrors.push('Please enter a valid email address');
    }
    
    // Validate phone format
    if (data.client.phone && !validatePhone(data.client.phone)) {
        validationErrors.push('Please enter a valid phone number');
    }
    
    // Validate card number format
    if (payment_info.card_number && !validateCardNumber(payment_info.card_number)) {
        validationErrors.push('Please enter a valid card number');
    }
    
    // Validate card expiration
    if (payment_info.card_expiration && !validateExpirationDate(payment_info.card_expiration)) {
        validationErrors.push('Please enter a valid expiration date (MM/YY)');
    }
    
    // If there are validation errors, show them in a single snackbar and return
    if (validationErrors.length > 0) {
        enqueueSnackbar(validationErrors.join('; '), { variant: 'error' });
        return;
    }
    
    // Ensure custom inputs are in the client object
    const subscriptionForm = document.querySelector('[pc="subscription-form"]') as HTMLFormElement;
    if (subscriptionForm && (!data.client.custom_inputs || !data.client.custom_inputs.length)) {
        data.client.custom_inputs = getCustomInputs(subscriptionForm);
    }
    
    // Combine client data with payment data
    const finalData = {
        ...data,
        payment_info
    };
    
    
    // Call API to process subscription with payment info
    processSubscription(finalData);
};

/*================================================
API CALL
================================================*/
const processSubscription = async (subscriptionData: any): Promise<void> => {
    ShowLoader(true);
    
    try {
        await ApiRequest({
            url: "/payments/subscriptionCheckout",
            method: "post",
            body: subscriptionData,
            setResponse: (response: any) => {
                ShowLoader(false);
                
                if (response.status === 'error') {
                    enqueueSnackbar(`Error processing subscription: ${response.message}`, { variant: 'error' });
                } else {
                    handleSubscriptionSuccess();
                    enqueueSnackbar('Subscription processed successfully', { variant: 'success' });
                    // Clear localStorage or specific keys if needed
                    localStorage.removeItem('subscription');
                }
            },
            onError: (error: any) => {
                ShowLoader(false);
                enqueueSnackbar(`Error processing subscription: ${error.message}`, { variant: 'error' });
            }
        });
    } catch (error) {
        ShowLoader(false);
        enqueueSnackbar(`Unexpected error processing subscription`, { variant: 'error' });
    }
};

// Add helper function to clean validation states before saving to localStorage
const cleanupDataForStorage = (data: any): any => {
    // Create a deep copy to avoid modifying the original object
    const cleanData = JSON.parse(JSON.stringify(data));
    
    // Remove validation state properties
    delete cleanData.isValidCardName;
    delete cleanData.isValidCardNumber;
    delete cleanData.isValidExpiration;
    delete cleanData.isValidCVV;
    delete cleanData.isValidEmail;
    delete cleanData.isValidPhone;
    
    // Remove any payment card data if it was stored previously
    delete cleanData.card_name;
    delete cleanData.card_number;
    delete cleanData.card_expiration;
    delete cleanData.card_cvv;
    
    return cleanData;
};

// Create a debounced version of getTaxes to prevent multiple calls during typing
const checkTaxesAndShipping = _.debounce(async () => {
    await getShippingMethods(getTaxes);
}, 500);

export const cancelPendingTaxCalculations = () => {
    checkTaxesAndShipping.cancel();
};

export const getTaxes = async () => {
    // Replace cart usage with subscription data
    const subscriptionData = JSON.parse(localStorage.getItem('subscription') || '{}');

    await ApiRequest({
        url: '/taxes/getTaxesV1',
        method: 'POST',
        body: {
            // Send the entire subscription for tax calculation
            cart: subscriptionData
        },
        setResponse: (response: any) => {
            if (response.status === 'success') {
                const updatedCart = { ...subscriptionData };
                // Update products if they exist
                if (updatedCart.products && response.products) {
                    updatedCart.products = updatedCart.products.map((product: any) => {
                        const taxedProduct = response.products.find(
                            (p: any) => p.order_product_id === product.order_product_id
                        );
                        if (taxedProduct) {
                            return {
                                ...product,
                                product_tax: taxedProduct.product_tax,
                                tax_amount: taxedProduct.tax_amount
                            };
                        }
                        return product;
                    });
                }

                const tax_rate = Number(response.tax_rate) || 0;
                const shipping_price = Number(updatedCart.shipping_price) || 0;
                const shippingTax = tax_rate * (shipping_price) / 100;
                
                updatedCart.tax_rate = tax_rate;
                updatedCart.total_tax = response.total_tax + shippingTax;
                
                // Update total price to include tax
                updatedCart.total_price = (parseFloat(updatedCart.subtotal) - parseFloat(updatedCart.total_discount || 0) + parseFloat(updatedCart.shipping_price || 0) + parseFloat(updatedCart.total_tax)).toFixed(2);
                
                // Save updated cart back to localStorage
                localStorage.setItem('subscription', JSON.stringify(updatedCart));
                localStorage.removeItem('tax_error');

                // --- Update popup header price fields with new total_price ---
                const summaryPrice = document.querySelector('[pc="summary-price"]');
                const recurrentAmount = document.querySelector('[pc="recurrent-amount"]');
                if (summaryPrice && updatedCart.total_price) {
                    summaryPrice.textContent = `$${parseFloat(updatedCart.total_price).toFixed(2)}`;
                }
                if (recurrentAmount && updatedCart.total_price) {
                    recurrentAmount.textContent = `$${parseFloat(updatedCart.total_price).toFixed(2)}`;
                }
                // -----------------------------------------------------------

                return updatedCart;
            } else {
                enqueueSnackbar(response.message, { variant: 'error' });
                localStorage.setItem('tax_error', response.message || 'Error calculating taxes');
            }
        }
    });
}


/**
 * Handle successful subscription result - show status and hide form
 */
export const handleSubscriptionSuccess = () => {
    const subscriptionStatusEl = document.querySelector('[pc="subscription-status"]') as HTMLElement;
    const subscriptionFormEl = document.querySelector('[pc="subscription-form"]') as HTMLElement;
    
    if (subscriptionStatusEl) {
        subscriptionStatusEl.classList.remove("hide_content");
    }
    if (subscriptionFormEl) {
        subscriptionFormEl.classList.add("hide_content");
    }
};
